/**
 * TermsReacceptBanner
 * -------------------
 * Soft (non-blocking) top banner shown to logged-in users whose
 * auth-user `user_metadata.terms_accepted_version` does not match the
 * current canonical Terms version (`CURRENT_TERMS_VERSION`).
 *
 * Why: 2026-02 legal audit (Fix #11). When BuildPilot updates its
 * Terms of Use we need a provable trail that each logged-in user
 * re-accepted the new version. We do NOT block their session - they
 * can still browse and use tools - but the banner stays until they
 * tap "I agree".
 *
 * Storage: Supabase Auth user_metadata, written via
 *   supabase.auth.updateUser({ data: { ... } })
 * No additional table / RLS change required.
 *
 * Mount: globally in App.js so it appears on every authenticated
 * route. Suppressed inside `/workspace/*` auth screens (login /
 * signup / forgot-password) so it doesn't overlap the empty shells.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { getSupabase } from '../../workspace/lib/supabaseClient';

const CURRENT_TERMS_VERSION = 'v2026-02';

const SUPPRESSED_PATH_PREFIXES = [
  '/workspace/login',
  '/workspace/signup',
  '/workspace/forgot-password',
  '/workspace/auth-callback',
];

export default function TermsReacceptBanner() {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const supa = getSupabase();
    if (!supa) return undefined;
    let cancelled = false;
    supa.auth.getUser().then(({ data }) => {
      if (!cancelled) setUser(data?.user || null);
    }).catch(() => {});
    const { data: sub } = supa.auth.onAuthStateChange((_evt, session) => {
      setUser(session?.user || null);
    });
    return () => {
      cancelled = true;
      sub?.subscription?.unsubscribe?.();
    };
  }, []);

  const accept = useCallback(async () => {
    if (accepting) return;
    setAccepting(true);
    try {
      const supa = getSupabase();
      if (!supa) return;
      await supa.auth.updateUser({
        data: {
          terms_accepted_version: CURRENT_TERMS_VERSION,
          terms_accepted_at: new Date().toISOString(),
        },
      });
      // Optimistic local update so the banner disappears immediately
      // even before the onAuthStateChange fires.
      setUser((u) => (u ? {
        ...u,
        user_metadata: {
          ...(u.user_metadata || {}),
          terms_accepted_version: CURRENT_TERMS_VERSION,
        },
      } : u));
    } finally {
      setAccepting(false);
    }
  }, [accepting]);

  if (!user || dismissed) return null;
  const acceptedVersion = user?.user_metadata?.terms_accepted_version;
  if (acceptedVersion === CURRENT_TERMS_VERSION) return null;
  if (SUPPRESSED_PATH_PREFIXES.some((p) => location.pathname.startsWith(p))) {
    return null;
  }

  return (
    <div
      role="alert"
      data-testid="terms-reaccept-banner"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 95,
        background: 'linear-gradient(135deg, #1d1d1f 0%, #2d2d2d 100%)',
        color: '#fff',
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        flexWrap: 'wrap',
        boxShadow: '0 2px 12px rgba(0,0,0,0.18)',
        fontSize: 13,
        lineHeight: 1.4,
      }}
    >
      <span style={{ maxWidth: 720, textAlign: 'center' }}>
        <strong>Updated Terms ({CURRENT_TERMS_VERSION}).</strong>{' '}
        We&apos;ve updated our{' '}
        <Link
          to="/terms"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#7fb3e8', textDecoration: 'underline' }}
        >
          Terms of Use
        </Link>{' '}
        and{' '}
        <Link
          to="/privacy"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#7fb3e8', textDecoration: 'underline' }}
        >
          Privacy Policy
        </Link>
        . Please confirm you agree to continue using BuildPilot.
      </span>
      <button
        type="button"
        onClick={accept}
        disabled={accepting}
        data-testid="terms-reaccept-accept"
        style={{
          padding: '7px 14px',
          background: '#1d8efe',
          color: '#fff',
          border: 'none',
          borderRadius: 999,
          fontSize: 12.5,
          fontWeight: 600,
          cursor: accepting ? 'wait' : 'pointer',
          opacity: accepting ? 0.7 : 1,
          whiteSpace: 'nowrap',
        }}
      >
        {accepting ? 'Saving…' : 'I agree'}
      </button>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Hide for now"
        data-testid="terms-reaccept-dismiss"
        style={{
          background: 'transparent',
          border: 'none',
          color: 'rgba(255,255,255,0.55)',
          cursor: 'pointer',
          padding: 4,
          display: 'inline-flex',
        }}
      >
        <X size={16} />
      </button>
    </div>
  );
}
